import create from 'zustand';
import type { State, WatchedHotjarFilter } from './types';

import axios from 'axios';
import { LocalStorage } from '@hotjar/insights-common/lib/storage-factory';
import { fetchHighlightsContentForRecordings } from '../../features/RecordingsPage/utils/fetchHighlightsContentForRecordings';
import {
  commentsByRecordingIdDtoToModel,
  labelsByRecordingIdDtoToModel,
} from '../../domains/highlights';
import { getSiteId } from '../../utils/getParamsFromUrlPathname';
import { DEFAULT_RELEVANCE_SCORE_FILTER_VALUE } from '../../constants/relevance';

export const COLUMNS = {
  comments: { label: 'Highlights', key: 'highlight-action-count', show: true, showInPanel: true },
  relevance: { label: 'Relevance', key: 'relevance-score', show: true, showInPanel: true },
  frustration: { label: 'Frustration', key: 'frustration-score', show: true, showInPanel: false },
  engagement: { label: 'Engagement', key: 'engagement-score', show: true, showInPanel: false },
  date: { label: 'Date', key: 'created', show: true, showInPanel: true },
  user: { label: 'User', key: 'visitor-uuid', show: true, showInPanel: true },
  country: { label: 'Country', key: 'country-code', show: true, showInPanel: true },
  action_count: { label: 'Actions', key: 'action-count', show: true, showInPanel: true },
  page_count: { label: 'Pages', key: 'page-count', show: true, showInPanel: true },
  duration: { label: 'Duration', key: 'duration', show: true, showInPanel: true },
  device: { label: 'Device', key: 'device', show: true, showInPanel: true },
  browser: { label: 'Browser', key: 'browser', show: true, showInPanel: true },
  operating_system: { label: 'Operating System', key: 'os', show: true, showInPanel: true },
  referrer_url: {
    label: 'Referrer URL',
    key: 'referrer-raw',
    show: false,
    isDisabled: true,
    showInPanel: true,
  },
  landing_url: { label: 'Landing URL', key: 'first-page-path-raw', show: true, showInPanel: true },
  exit_url: { label: 'Exit URL', key: 'last-page-path-raw', show: true, showInPanel: true },
};

export const initialState = {
  // Scope Set-Able
  addFilterTippyInstance: null,
  automatedFilterUpdateFlag: false,

  // Model Set-Able
  commentsByRecordingId: {},
  columns: COLUMNS,
  currentPage: 1,
  currentSegment: null,
  editedSegment: null,
  effectiveDateRangeFilter: null,
  exportBannerKind: null,
  exportBannerMessage: null,
  hotjarFilters: {
    watched: !!LocalStorage.get('shouldShowNotWatchedByDefault')
      ? 'not_watched'
      : ('all' as WatchedHotjarFilter),
    relevanceScore: [...DEFAULT_RELEVANCE_SCORE_FILTER_VALUE],
  },
  isOverviewExperimentEnabled: false,
  itemsPerPage: (LocalStorage.get('recordingsItemsPerPage') as number) ?? 50,
  jsonFilters: null,
  labelsByRecordingId: {},
  loaded: false,
  previousPage: 1,
  recordings: [],
  recordingsRequestTimeRange: null,
  recordingMode: null,
  recordingSettings: {
    excludeMinimalActivity: null,
    excludeShortRecordings: null,
    recordingCaptureKeystrokes: null,
    recordingTargeting: {
      rules: [],
      attributeRules: [],
      urlRules: [],
      triggerRules: [],
    },
  },
  recordingsFetchedWithV3Api: true,
  searching: true,
  segments: [],
  selectedAllRecordings: false,
  selectedRecordings: [],
  showExportBanner: false,
  showNotWatchedPopoverInfo: false,
  site: null,
  sortBy: null,
  sortInitialOption: 'relevance_score',
  sortInitialReverse: true,
  totalHits: 0,
  totalRecordingsCount: 0,
  trackingCode: null,
  trackPageChange: false,

  // Overview
  overviewData: {
    recordingsRelevant: [],
    recordingsWithRageClicks: [],
    recordingsEngaged: [],
    recordingsWithErrors: [],
    recordingsWithUturns: [],
    recordingsFrustrated: [],
    recordingsRelevantTotalHits: 0,
    recordingsWithRageClicksTotalHits: 0,
    recordingsEngagedTotalHits: 0,
    recordingsWithErrorsTotalHits: 0,
    recordingsWithUturnsTotalHits: 0,
    recordingsFrustratedTotalHits: 0,
  },
  widgetsLoaded: false,
};

export const useRecordingsListStore = create<State>((set, get) => ({
  ...initialState,
  setAutomatedFilterUpdateFlag: (automatedFilterUpdateFlag) => set({ automatedFilterUpdateFlag }),
  setAddFilterTippyInstance: (addFilterTippyInstance) => set({ addFilterTippyInstance }),
  setColumns: (columns) => set({ columns }),
  setCurrentPage: (currentPage) => set({ currentPage }),
  setCurrentSegment: (currentSegment) => set({ currentSegment }),
  setCommentsByRecordingId: (commentsByRecordingId) => set({ commentsByRecordingId }),
  setLabelsByRecordingId: (labelsByRecordingId) => set({ labelsByRecordingId }),
  setEditedSegment: (editedSegment) => set({ editedSegment }),
  setEffectiveDateRangeFilter: (effectiveDateRangeFilter) => set({ effectiveDateRangeFilter }),
  setExportBannerKind: (kind) => set({ exportBannerKind: kind }),
  setExportBannerMessage: (message) => set({ exportBannerMessage: message }),
  setHotjarFilters: (hotjarFilters) => set({ hotjarFilters }),
  setIsOverviewExperimentEnabled: (isOverviewExperimentEnabled) =>
    set({ isOverviewExperimentEnabled }),
  setItemsPerPage: (itemsPerPage) => set({ itemsPerPage }),
  setJsonFilters: (jsonFilters) => set({ jsonFilters }),
  setLoaded: (loaded) => set({ loaded }),
  setPreviousPage: (previousPage) => set({ previousPage }),
  setRecordings: (recordings) => set({ recordings }),
  setRecordingsRequestTimeRange: (recordingsRequestTimeRange) =>
    set({ recordingsRequestTimeRange }),
  setRecordingMode: (recordingMode) => set({ recordingMode }),
  setRecordingSettings: (recordingSettings) => set({ recordingSettings }),
  setRecordingsFetchedWithV3Api: (recordingsFetchedWithV3Api) =>
    set({ recordingsFetchedWithV3Api }),
  setSearching: (searching) => set({ searching }),
  setSelectedAllRecordings: (selectedAllRecordings) => set({ selectedAllRecordings }),
  setSelectedRecordings: (selectedRecordings) => set({ selectedRecordings }),
  setShowExportBanner: (showExportBanner) => set({ showExportBanner }),
  setShowNotWatchedPopoverInfo: (showNotWatchedPopoverInfo) => set({ showNotWatchedPopoverInfo }),
  setSite: (site) => set({ site }),
  setSegments: (segments) => set({ segments }),
  setSortBy: (sortBy) => set({ sortBy }),
  setSortInitialOption: (sortInitialOption) => set({ sortInitialOption }),
  setSortInitialReverse: (sortInitialReverse) => set({ sortInitialReverse }),
  setTotalHits: (totalHits) => set({ totalHits }),
  setTotalRecordingsCount: (totalRecordingsCount) => set({ totalRecordingsCount }),
  setTrackingCode: (trackingCode) => set({ trackingCode }),
  setTrackPageChange: (trackPageChange) => set({ trackPageChange }),
  // Overview
  setOverviewData: (overviewData) => set({ overviewData }),
  setWidgetsLoaded: (widgetsLoaded) => set({ widgetsLoaded }),

  /**
   * If recordings are enabled and TC is not installed - we show the new nudge.
   */
  isTrackingCodePromptVisible() {
    const trackingCode = get().trackingCode;

    return trackingCode ? !trackingCode.installed : false;
  },
  /**
   * If recordings are enabled, TC is installed, recordings are ON
   * but there's no data yet - we show the empty state card.
   */
  isEmptyStateCardVisible() {
    const { trackingCode, totalRecordingsCount, recordingMode } = get();

    return Boolean(
      trackingCode &&
        trackingCode.installed &&
        totalRecordingsCount === 0 &&
        recordingMode !== 'off',
    );
  },
  /**
   * We have this as a separate method as we'll be introducing multiple teaser modes -
   * - tools are enabled but TC is not installed
   * - tools are enabled and TC is installed, but there is still no data
   */
  inTeaserMode() {
    const { isTrackingCodePromptVisible, isEmptyStateCardVisible } = get();

    return isTrackingCodePromptVisible() || isEmptyStateCardVisible();
  },
  showActions() {
    const { inTeaserMode, totalRecordingsCount } = get();

    return !inTeaserMode() && totalRecordingsCount > 0;
  },
  retrieveHighlightsCommentsAndLabels: async () => {
    const { recordings, setCommentsByRecordingId, setLabelsByRecordingId } = get();

    if (recordings && recordings.length > 0) {
      const { commentsByRecordingId, labelsByRecordingId } =
        await fetchHighlightsContentForRecordings(recordings);

      setCommentsByRecordingId(commentsByRecordingIdDtoToModel(commentsByRecordingId));
      setLabelsByRecordingId(labelsByRecordingIdDtoToModel(labelsByRecordingId));
    }
  },
  /**
   * Performs the retrieval of the total recordings count for the current site.
   *
   * This count represents the total number of recordings that the site has, regardless of filters or other options.
   */
  retrieveTotalRecordingsCount: async (): Promise<number> => {
    const { setTotalRecordingsCount } = get();

    // The actual call to Quantz requires filters in the request in order to not let it default to its own values.
    // This is why we send a single filter for 365 days (1 year), which is also the max retention period for recordings.
    const {
      data: {
        data: { result: totalRecordingsCount },
      },
    } = await axios.post(`/api/dashboard/v1/sites/${getSiteId()}/query`, {
      query: { operation: 'count', session_filters: { DAYS_AGO: { created: 365 } } },
    });

    setTotalRecordingsCount(totalRecordingsCount);

    return totalRecordingsCount;
  },
}));
